/**
 * Jumlah guest per unit qty untuk produk activity/spa.
 * Cart lama (vuex-persistedstate) belum punya guest_list, jadi selalu dinormalisasi
 * ke panjang qty dengan default guest_included.
 * @param {Array} guest_list nilai tersimpan di cart, boleh kosong
 * @param {Number} qty jumlah unit produk
 * @param {Number} guest_included kapasitas guest per 1 unit produk
 * @returns {Array} array angka sepanjang qty, tiap nilai di rentang 1..guest_included
 */
export function normalizeGuestList(guest_list, qty, guest_included) {
  const max_guest = guest_included > 0 ? Number(guest_included) : 1
  const total_qty = qty > 0 ? Number(qty) : 0
  const saved_list = Array.isArray(guest_list) ? guest_list : []

  const normalized = []
  for (let i = 0; i < total_qty; i++) {
    const saved_guest = Number(saved_list[i])
    if (!saved_guest || saved_guest < 1) normalized.push(max_guest)
    else normalized.push(Math.min(saved_guest, max_guest))
  }

  return normalized
}

/**
 * Pecah satu item cart menjadi satu object per unit qty untuk payload tx-inquiry.
 * @param {Object} base field produk yang sama untuk semua unit
 * @param {Array} guest_list hasil normalizeGuestList
 * @returns {Array} [{ ...base, qty: 1, total_guest: n }]
 */
export function expandProductByGuest(base, guest_list) {
  return guest_list.map(guest => Object.assign({}, base, { qty: 1, total_guest: guest }))
}

/**
 * Label jumlah unit produk untuk ringkasan cart, mis. "3 items" atau "2 sessions".
 * @param {Number} qty jumlah unit produk
 * @param {Object} unit objek unit produk dari API, boleh kosong
 * @returns {String} teks jumlah beserta satuannya
 */
export function formatQtyText(qty, unit) {
  const total_qty = qty > 0 ? Number(qty) : 0
  const unit_name = unit && unit.unit ? unit.unit.toLowerCase() : 'item'

  return total_qty + ' ' + (total_qty > 1 ? unit_name + 's' : unit_name)
}

/**
 * Kebalikan expandProductByGuest: satukan lagi baris tx_detail yang identik supaya
 * ringkasan transaksi tidak menampilkan produk yang sama berulang kali.
 * Hanya baris qty 1 yang digabung karena itu bentuk yang dihasilkan expandProductByGuest;
 * baris qty > 1 dibiarkan apa adanya supaya total_guest-nya tidak salah dijumlahkan ulang.
 * @param {Array} tx_detail_list baris transaksi dari API
 * @returns {Array} baris tergabung dengan qty, subtotal_sell, guest_list, guest_display, total_guest_all
 */
export function groupTxDetailByProduct(tx_detail_list) {
  const rows = Array.isArray(tx_detail_list) ? tx_detail_list : []
  const grouped = []
  const index_by_key = {}

  rows.forEach(row => {
    const guest = readRowGuest(row)

    if (Number(row.qty) !== 1) {
      grouped.push(Object.assign({}, row, { guest_list: guest > 0 ? [guest] : [] }))
      return
    }

    const key = buildGroupKey(row)
    if (index_by_key[key] === undefined) {
      index_by_key[key] = grouped.length
      grouped.push(Object.assign({}, row, {
        qty: 1,
        subtotal_sell: Number(row.subtotal_sell),
        guest_list: guest > 0 ? [guest] : []
      }))
      return
    }

    const target = grouped[index_by_key[key]]
    target.qty += 1
    target.subtotal_sell += Number(row.subtotal_sell)
    if (guest > 0) target.guest_list.push(guest)
  })

  return grouped.map(row => {
    const total_guest_all = row.guest_list.reduce((total, guest) => total + guest, 0)
    return Object.assign({}, row, {
      total_guest_all,
      guest_display: total_guest_all > 0 ? row.guest_list.join(', ') : ''
    })
  })
}

/** Jumlah guest bisa datang di root baris atau ikut di dalam blob detail */
function readRowGuest(row) {
  if (row.total_guest) return Number(row.total_guest)
  const detail = row.detail
  if (detail && typeof detail === 'object' && detail.total_guest) return Number(detail.total_guest)
  return 0
}

/** Semua field yang benar-benar dirender ikut jadi kunci supaya baris yang tampil beda tidak menyatu */
function buildGroupKey(row) {
  const detail = row.detail && typeof row.detail === 'object' ? row.detail : {}

  return [
    row.product_id || row.product_name,
    row.variant_name || '',
    row.service_date || '',
    row.subtotal_sell,
    detail.place_choice || '',
    detail.therapist_gender || '',
    row.remark || '',
    row.notes || ''
  ].join('|')
}



// WEBPACK FOOTER //
// ./src/utils/activityCart.js